"use client";
import { Image } from "@/components/Image";
import { dayjs } from "@/lib/dayjs";
import { formatPrice } from "@/lib/formatPrice";
import { getAltName } from "@/lib/getAltName";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import AnimateHeight from "react-animate-height";
import { PrimaryButton } from "ui/components";
import { NoPhoto } from "../icons/NoPhoto";
import { TagGuaranteed } from "../tags/TagGuaranteed";
import { TagHemerys } from "../tags/TagHemerys";
import { TagPrimo } from "../tags/TagPrimo";

type Props = {
  travel: {
    id: number;
    alias: string;
    title: string;
    brandCustomerTitle: string | null;
  };
  image: {
    name: string;
    realname: string;
    caption: string;
  } | null;
  departure: {
    id: number;
    garanti: boolean;
    defaultPrice: number;
    primoPrice?: number | null;
    dateStart: string;
    dateEnd: string;
    speakerType: string | null;
    registrationStatus: "open" | "closed" | "soon" | "onDemand";
    speakers: Array<{
      id: number;
      alias?: string | null;
      firstname: string;
      lastname: string;
      imageName?: string | null;
      imageRealname?: string | null;
    }>;
  };
};
export const TravelCardNew = ({ travel, image, departure }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [height, setHeight] = useState(40);
  const [isHover, setIsHover] = useState(false);
  const speaker = departure.speakers.filter((s) => s.id !== 1)[0];

  useEffect(() => {
    if (ref.current) {
      ref.current.classList.remove("line-clamp-2");
      const height = ref.current.offsetHeight;
      setHeight(height);
      ref.current.classList.add("line-clamp-2");
    }
  }, []);

  return (
    <Link
      href={`/voyage/${travel.alias}-${travel.id}/`}
      className="group/card no-underline w-72 h-[364px] bg-white border border-bg-2 rounded-lg shadow-c1 flex flex-col p-1.5 text-tx-1"
      onMouseEnter={() => {
        setIsHover(true);
        ref.current?.classList.remove("line-clamp-2");

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      }}
      onMouseLeave={() => {
        setIsHover(false);
        timeoutRef.current = setTimeout(() => {
          ref.current?.classList.add("line-clamp-2");
        }, 300);
      }}
    >
      <div className="flex-[0_1_204px] shrink w-full rounded-sm overflow-hidden flex items-center justify-center relative">
        {image && image.name !== "" ? (
          <>
            <Image
              className="w-full transition-transform"
              imgClassName=""
              alt={image.caption || ""}
              image={{
                id: travel.id.toString(),
                name: image.name,
                realname: image.realname || "",
                type: "travel",
              }}
              height={{
                type: "ratio",
                value: 80,
              }}
              sizes="640px"
              objectFit="cover"
            />
            <div className="absolute inset-0 shadow-[0_0_32px_0_rgba(0,0,0,0.24)_inset] z-2" />
          </>
        ) : (
          <div className="relative overflow-hidden pb-[80%] w-full">
            <NoPhoto className="absolute top-1/4 left-1/4 w-1/2 h-1/2" />
          </div>
        )}
        <div className="absolute top-1.5 left-1.5 flex gap-1.5 z-3">
          {departure.garanti && <TagGuaranteed />}
          {departure.primoPrice && <TagPrimo />}
        </div>
        {travel.brandCustomerTitle === "Hémérys" && <TagHemerys className="absolute bottom-1.5 right-1.5 z-3" />}
      </div>
      <div className="flex flex-col shrink-0 gap-[10px] auto-rows-auto p-1.5">
        <AnimateHeight duration={300} height={isHover ? height : 40}>
          <h3 ref={ref} className="text-base leading-5 font-semibold line-clamp-2">
            {travel.title}
          </h3>
        </AnimateHeight>
        <p className="relative text-xs leading-[14px] before:absolute before:bg-tx-2 before:left-0 before:top-px before:h-3 before:w-px pl-1.5">
          {`${dayjs(departure.dateStart).format("DD MMM")} - ${dayjs(departure.dateEnd).format("DD MMM YYYY")}`}
        </p>
        {speaker && (
          <div className="h-8 flex gap-2 items-center">
            <div className="w-8 h-8 shrink-0 rounded-full overflow-hidden">
              <Image
                alt={speaker.imageRealname || ""}
                image={{
                  id: speaker.id.toString(),
                  name: speaker.imageName || "",
                  realname: speaker.imageRealname || "",
                  type: "speaker",
                }}
                height={{
                  type: "ratio",
                  value: 100,
                }}
                sizes="182px"
                objectFit="cover"
              />
            </div>
            <p className="text-sm font-medium">
              {speaker.firstname} {speaker.lastname}
            </p>
          </div>
        )}
        {speaker === undefined && (
          <div className="h-8 flex gap-2 items-center">
            <div className="w-8 h-8 shrink-0 rounded-full overflow-hidden">
              {departure?.speakerType === "local" || travel.brandCustomerTitle === "Orients" ? (
                <div className="w-8 h-8 shrink-0 rounded-full overflow-hidden bg-bg-1 border border-tx-2 p-[7px] fill-tx-2 stroke-tx-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      stroke="#C1B1AC"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.33333"
                      d="M11.6665 13.8327v-1.3889c0-.7367-.2634-1.4433-.7322-1.9642-.4689-.52093-1.10475-.81358-1.7678-.81358h-5c-.66304 0-1.29892.29265-1.76776.81358-.46884.5209-.73224 1.2275-.73224 1.9642v1.3889m5-6.66668c1.38072 0 2.5-1.11929 2.5-2.5 0-1.38072-1.11928-2.5-2.5-2.5-1.38071 0-2.5 1.11928-2.5 2.5 0 1.38071 1.11929 2.5 2.5 2.5Z"
                    />
                    <path
                      fill="#C1B1AC"
                      stroke="#fff"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth=".666667"
                      d="m11.3787 15.328.0003.0005c.2972.4509.9444.4509 1.2417 0l.0003-.0005c.4422-.6731.8233-1.2432 1.1507-1.7329.5986-.8955 1.0175-1.5222 1.3013-2.0176.2235-.3903.3749-.7182.4684-1.0496.0941-.3337.1251-.65534.1251-1.0279 0-2.10159-1.6265-3.83333-3.6667-3.83333-2.04015 0-3.66663 1.73174-3.66663 3.83333 0 .37256.03098.6942.12509 1.0279.09348.3314.2449.6593.46843 1.0496.28379.4954.70267 1.1221 1.30131 2.0176.3274.4897.7085 1.0598 1.1507 1.7329Z"
                    />
                    <path
                      fill="#fff"
                      d="M13.3886 9.43102c0 .76768-.6218 1.38998-1.3889 1.38998-.767 0-1.3889-.6223-1.3889-1.38998s.6219-1.39 1.3889-1.39c.7671 0 1.3889.62232 1.3889 1.39Z"
                    />
                  </svg>
                </div>
              ) : (
                <div className="w-8 h-8 shrink-0 rounded-full overflow-hidden bg-bg-1 border border-tx-2 p-[7px]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      fill="#C1B1AC"
                      fillRule="evenodd"
                      d="M7.3234 7.9088C7.0439 6.3268 5.6623 5.125 4 5.125 2.136 5.125.625 6.636.625 8.5S2.136 11.875 4 11.875c.9646 0 1.8347-.4047 2.4498-1.0535-.5156-.5291-.6052-1.434.0077-2.0468l.8659-.8659ZM7.2564 9.39a3.3503 3.3503 0 0 1-.202.5477c-.0485-.1497-.0212-.3244.1102-.4559l.0918-.0918ZM2.125 8.5c0-1.0355.8395-1.875 1.875-1.875 1.0355 0 1.875.8395 1.875 1.875 0 1.0355-.8395 1.875-1.875 1.875-1.0355 0-1.875-.8395-1.875-1.875Zm-.75 4.5c-.9221 0-1.7883.4077-2.4136 1.1025-.623.6923-.9614 1.6157-.9614 2.5642v1.4583a.75.75 0 0 0 1.5 0v-1.4583c0-.5987.2148-1.159.5763-1.5607.3592-.3991.8284-.606 1.2987-.606h5.25c.4703 0 .9395.2069 1.2987.606.3615.4017.5763.962.5763 1.5607v1.4583a.75.75 0 0 0 1.5 0v-1.4583c0-.9485-.3384-1.8719-.9614-2.5642C8.4133 13.4077 7.5471 13 6.625 13h-5.25Z"
                      clipRule="evenodd"
                    />
                    <path
                      stroke="#C1B1AC"
                      strokeWidth="1.5"
                      d="M8.7071 8.2929 7.3414 9.6586c-.126.126-.0367.3414.1414.3414H17.5c.5523 0 1-.4477 1-1V3c0-.5523-.4477-1-1-1H10c-.5523 0-1 .4477-1 1v4.5858a1 1 0 0 1-.2929.707Z"
                    />
                    <circle cx="12" cy="6" r="1" fill="#C1B1AC" />
                    <circle cx="15" cy="6" r="1" fill="#C1B1AC" />
                  </svg>
                </div>
              )}
            </div>
            <p className="text-sm font-medium">Avec {getAltName(departure?.speakerType, travel.brandCustomerTitle)}</p>
          </div>
        )}
        <div className="flex items-center gap-1 h-5">
          {(departure.registrationStatus === "open" || departure.registrationStatus === "onDemand") && (
            <>
              <p className="text-xs">À partir de</p>
              <p className="text-lg leading-5 font-bold text-pr-1">
                {formatPrice(
                  departure.primoPrice ? departure.defaultPrice - departure.primoPrice : departure.defaultPrice,
                )}
              </p>
              {departure.primoPrice && (
                <p className="text-xs line-through text-tx-2">{formatPrice(departure.defaultPrice)}</p>
              )}
            </>
          )}
        </div>
      </div>
      <AnimateHeight duration={300} height={isHover ? 54 : 0} className="shrink-0" contentClassName="px-1.5 pb-1.5">
        <PrimaryButton as="span" className="w-full" direction="up">
          Voir le voyage
        </PrimaryButton>
      </AnimateHeight>
    </Link>
  );
};
